import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import api from '../utils/api';
import {
  CheckCircle, User, GraduationCap, FileText, Send, Loader2,
  Clock, AlertCircle, Star
} from 'lucide-react';

const RecommenderPortal = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [portalData, setPortalData] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    letter_content: '',
    how_long_known: '',
    capacity_known: '',
    academic_rating: '',
    character_rating: '',
    additional_comments: ''
  });

  useEffect(() => {
    loadPortalData();
  }, [token]);

  const loadPortalData = async () => {
    try {
      const response = await api.get(`/recommenders/portal/${token}`);
      setPortalData(response.data);
      
      if (response.data.status === 'submitted') {
        setSubmitted(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired link');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.letter_content.trim()) {
      toast({ title: 'Error', description: 'Please write a recommendation letter', variant: 'destructive' });
      return;
    }
    
    if (formData.letter_content.length < 200) {
      toast({ title: 'Error', description: 'Recommendation letter should be at least 200 characters', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/recommenders/portal/${token}/submit`, formData);
      setSubmitted(true);
      toast({ title: 'Success', description: 'Your recommendation has been submitted!' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to submit recommendation', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-emerald-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading recommendation portal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Link Invalid or Expired</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-sm text-gray-500">
              Please contact the student to request a new recommendation link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <div className="bg-emerald-100 rounded-full p-4 w-20 h-20 mx-auto mb-6">
              <CheckCircle className="h-12 w-12 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h2>
            <p className="text-gray-600 mb-4">
              Your recommendation for <strong>{portalData?.student_name}</strong> has been successfully submitted.
            </p>
            <p className="text-sm text-gray-500">
              The student has been notified and your letter will be included in their application to{' '}
              <strong>{portalData?.college_name}</strong>.
            </p>
            <div className="mt-6 pt-6 border-t">
              <p className="text-xs text-gray-400">You can close this window now.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-emerald-600 font-bold text-2xl mb-2">
            <GraduationCap className="h-8 w-8" />
            Wayo Education
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Recommendation Portal</h1>
          <p className="text-gray-600 mt-2">Submit a letter of recommendation</p>
        </div>

        {/* Student Info Card */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="bg-emerald-100 p-3 rounded-full">
                <User className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Recommendation requested by</p>
                <h3 className="text-xl font-semibold text-gray-900">{portalData?.student_name}</h3>
                <p className="text-gray-600 mt-1">
                  For application to <strong>{portalData?.college_name}</strong>
                </p>
                <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                  <Clock className="h-4 w-4" />
                  Requested {portalData?.created_at ? new Date(portalData.created_at).toLocaleDateString() : 'recently'}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recommendation Form */}
        <form onSubmit={handleSubmit}>
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Letter of Recommendation
              </CardTitle>
              <CardDescription>
                Please provide a detailed letter describing the student's qualities, achievements, and potential.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Background Questions */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>How long have you known this student?</Label>
                  <Input
                    placeholder="e.g., 2 years"
                    value={formData.how_long_known}
                    onChange={(e) => setFormData({ ...formData, how_long_known: e.target.value })}
                    data-testid="how-long-known"
                  />
                </div>
                <div>
                  <Label>In what capacity?</Label>
                  <Input
                    placeholder="e.g., As their AP Chemistry teacher"
                    value={formData.capacity_known}
                    onChange={(e) => setFormData({ ...formData, capacity_known: e.target.value })}
                    data-testid="capacity-known"
                  />
                </div>
              </div>

              {/* Rating Section */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-2">
                    <Star className="h-4 w-4 text-amber-500" />
                    Academic Performance Rating
                  </Label>
                  <Select
                    value={formData.academic_rating}
                    onValueChange={(value) => setFormData({ ...formData, academic_rating: value })}
                  >
                    <SelectTrigger data-testid="academic-rating">
                      <SelectValue placeholder="Select rating" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="excellent">Excellent (Top 5%)</SelectItem>
                      <SelectItem value="very_good">Very Good (Top 10%)</SelectItem>
                      <SelectItem value="good">Good (Top 25%)</SelectItem>
                      <SelectItem value="average">Average (Top 50%)</SelectItem>
                      <SelectItem value="below_average">Below Average</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <Star className="h-4 w-4 text-amber-500" />
                    Character & Integrity Rating
                  </Label>
                  <Select
                    value={formData.character_rating}
                    onValueChange={(value) => setFormData({ ...formData, character_rating: value })}
                  >
                    <SelectTrigger data-testid="character-rating">
                      <SelectValue placeholder="Select rating" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="excellent">Excellent</SelectItem>
                      <SelectItem value="very_good">Very Good</SelectItem>
                      <SelectItem value="good">Good</SelectItem>
                      <SelectItem value="average">Average</SelectItem>
                      <SelectItem value="below_average">Below Average</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Main Letter */}
              <div>
                <Label className="text-base font-semibold">
                  Recommendation Letter <span className="text-red-500">*</span>
                </Label>
                <p className="text-sm text-gray-500 mb-2">
                  Please include specific examples of the student's academic abilities, character, 
                  leadership qualities, and any notable achievements. Minimum 200 characters.
                </p>
                <Textarea
                  rows={12}
                  placeholder={`Dear Admissions Committee,

I am writing to recommend ${portalData?.student_name} for admission to ${portalData?.college_name}...

[Continue with specific examples of academic excellence, character, leadership, and why this student would be a valuable addition to your institution]`}
                  value={formData.letter_content}
                  onChange={(e) => setFormData({ ...formData, letter_content: e.target.value })}
                  className="font-serif"
                  data-testid="letter-content"
                />
                <p className="text-xs text-gray-400 mt-1 text-right">
                  {formData.letter_content.length} characters
                  {formData.letter_content.length < 200 && formData.letter_content.length > 0 && (
                    <span className="text-amber-600"> (minimum 200 required)</span>
                  )}
                </p>
              </div>

              {/* Additional Comments */}
              <div>
                <Label>Additional Comments (Optional)</Label>
                <Textarea
                  rows={3}
                  placeholder="Any additional information you'd like to share..."
                  value={formData.additional_comments}
                  onChange={(e) => setFormData({ ...formData, additional_comments: e.target.value })}
                  data-testid="additional-comments"
                />
              </div>
            </CardContent>
          </Card>

          {/* Disclaimer */}
          <Card className="mb-6 border-amber-200 bg-amber-50">
            <CardContent className="pt-4">
              <p className="text-sm text-amber-800">
                <strong>Important:</strong> By submitting this recommendation, you confirm that the 
                information provided is accurate and reflects your honest assessment of the student. 
                This recommendation will be shared with the college admissions office as part of the 
                student's application.
              </p>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full h-12 text-lg bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
            disabled={submitting}
            data-testid="submit-recommendation-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="h-5 w-5 mr-2" />
                Submit Recommendation
              </>
            )}
          </Button>
        </form>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-8">
          © 2026 Wayo Education. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default RecommenderPortal;
